import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  StackedCellText,
} from '@/components/common/DataTable';
import { RowActionButton } from '@/components/common/RowActions';
import { formatDisplayDate } from '@/utils/date';
import { formatCount, formatNumber } from '@/utils/number';
import { FiscalYearBadge } from './FiscalYearBadge';
import { PersonNameCell } from './PersonNameCell';
import { VerifyStatusBadge } from './VerifyStatusBadge';
import type { ExpenseHeader } from '../types/billing.types';

interface ExpenseApprovalColumnDeps {
  onOpen: (expenseId: number) => void;
  searchText: string | undefined;
}

export function getExpenseApprovalColumns(
  deps: ExpenseApprovalColumnDeps,
): ColumnDef<ExpenseHeader, unknown>[] {
  return [
    createSerialColumn<ExpenseHeader>(),
    {
      accessorKey: 'name',
      header: 'ZM Name',
      cell: ({ row }) => (
        <PersonNameCell
          name={row.original.name}
          designationName={row.original.designationName}
          mobile={row.original.userMobile}
          searchText={deps.searchText}
        />
      ),
    },
    {
      id: 'expenseDate',
      header: 'Expense Date',
      cell: ({ row }) => (
        <StackedCellText
          primary={formatDisplayDate(row.original.expenseDate)}
          secondary={
            row.original.fiscalYear ? (
              <FiscalYearBadge fiscalYear={row.original.fiscalYear} />
            ) : null
          }
        />
      ),
    },
    {
      accessorKey: 'claimedTotal',
      header: 'Claimed (Tk)',
      cell: ({ row }) => (
        <StackedCellText
          primary={<span className="tabular-nums">{formatNumber(row.original.claimedTotal)}</span>}
          secondary={formatCount(row.original.lineCount, 'expense item')}
        />
      ),
    },
    {
      accessorKey: 'approvedTotal',
      header: 'Approved (Tk)',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.approvedTotal)}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <div className="flex min-w-36 items-center gap-2">
          <VerifyStatusBadge status={row.original.approveStatus} decisionKind="approve" />
          {row.original.isLocked ? (
            <span className="text-[11px] text-muted-foreground">locked</span>
          ) : null}
        </div>
      ),
    },
    createActionsColumn<ExpenseHeader>((expense) => (
      <RowActionButton
        label="View"
        ariaLabel={`View the expense of ${expense.name}`}
        onClick={() => deps.onOpen(expense.expenseId)}
      />
    )),
  ];
}
